import { useNavigate } from '@tanstack/react-router';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  LoadingSkeleton,
} from '@nexora/design-system';
import {
  Puzzle,
  Webhook,
  Download,
  Upload,
  ArrowRight,
} from 'lucide-react';
import { useIntegrationOverview } from './useIntegration';
import type { ActivityEvent } from './useIntegration';

/* ── Helpers ── */

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const statusVariant: Record<string, 'info' | 'success' | 'error' | 'neutral'> = {
  pending: 'info',
  processing: 'info',
  completed: 'success',
  failed: 'error',
};

const statusLabel: Record<string, string> = {
  pending: 'Pendente',
  processing: 'Processando',
  completed: 'Concluído',
  failed: 'Falhou',
};

/* ── Props ── */

interface CardLinkProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  count: number | string;
  lastItem?: string;
  to: string;
}

function CardLink({
  icon,
  title,
  description,
  count,
  lastItem,
  to,
}: CardLinkProps): JSX.Element {
  const navigate = useNavigate();

  return (
    <div
      className="integration-card-wrapper"
      onClick={() => navigate({ to })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate({ to }); }}
      style={{ cursor: 'pointer' }}
    >
      <Card variant="elevated" padding="1.25rem" className="integration-card">
      <CardHeader>
        <div className="card-header-content">
          <span className="card-icon">{icon}</span>
          <span className="card-count">{count}</span>
        </div>
      </CardHeader>
      <CardBody>
        <h3 className="card-title">{title}</h3>
        <p className="card-desc">{description}</p>
        {lastItem && <p className="card-last">{lastItem}</p>}
        <div className="card-action">
          <span>Acessar</span>
          <ArrowRight size={14} />
        </div>
      </CardBody>
      </Card>
    </div>
  );
}

function CardLinkSkeleton(): JSX.Element {
  return (
    <Card variant="elevated" padding="1.25rem" className="integration-card">
      <CardHeader>
        <div className="card-header-content">
          <LoadingSkeleton width="32px" height="32px" borderRadius="8px" />
          <LoadingSkeleton width="48px" height="24px" />
        </div>
      </CardHeader>
      <CardBody>
        <LoadingSkeleton width="70%" height="20px" />
        <div style={{ marginTop: '0.5rem' }}>
          <LoadingSkeleton width="100%" height="14px" />
        </div>
        <div style={{ marginTop: '0.25rem' }}>
          <LoadingSkeleton width="60%" height="14px" />
        </div>
        <div style={{ marginTop: '1rem' }}>
          <LoadingSkeleton width="90px" height="14px" />
        </div>
      </CardBody>
    </Card>
  );
}

/* ── Activity Row ── */

interface ActivityRowProps {
  event: ActivityEvent;
}

function ActivityRow({ event }: ActivityRowProps): JSX.Element {
  return (
    <div className="activity-row">
      <span className="activity-kind">
        {event.kind === 'export' ? (
          <Download size={14} />
        ) : (
          <Upload size={14} />
        )}
      </span>
      <span className="activity-desc" title={event.description}>
        {event.description}
      </span>
      <span>
        <Badge variant={statusVariant[event.status] ?? 'neutral'}>
          {statusLabel[event.status] ?? event.status}
        </Badge>
      </span>
      <span className="activity-date">{formatDate(event.createdAt)}</span>
    </div>
  );
}

/* ── Main Page ── */

export function IntegrationPage(): JSX.Element {
  const { data, isLoading, isError } = useIntegrationOverview();

  const appsCount = data?.apps.length ?? 0;
  const webhooksCount = data?.webhooks.length ?? 0;
  const exportsCount = data?.exports.length ?? 0;
  const importsCount = data?.imports.length ?? 0;

  const lastExport =
    data && data.exports.length > 0 && data.exports[0]
      ? `Último: ${data.exports[0].type} — ${statusLabel[data.exports[0].status] ?? data.exports[0].status}`
      : undefined;

  const lastImport =
    data && data.imports.length > 0 && data.imports[0]
      ? `Último: ${data.imports[0].fileName} — ${statusLabel[data.imports[0].status] ?? data.imports[0].status}`
      : undefined;

  return (
    <div className="integration-dashboard">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Integrações</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Integrações, credenciais e webhooks.</p>
        </div>
      </div>

      {/* Error Banner */}
      {isError && (
        <Card variant="outlined" padding="1rem" className="error-banner">
          <CardBody>
            <p className="error-text">
              Não foi possível carregar alguns dados do dashboard.
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
              Tentar novamente
            </Button>
          </CardBody>
        </Card>
      )}

      {/* Grid de Cards */}
      <div className="cards-grid">
        {isLoading ? (
          <>
            <CardLinkSkeleton />
            <CardLinkSkeleton />
            <CardLinkSkeleton />
            <CardLinkSkeleton />
          </>
        ) : (
          <>
            <CardLink
              icon={<Puzzle size={22} />}
              title="Aplicações"
              description="Gerencie integrações com aplicações externas."
              count={appsCount}
              to="/integrations/applications"
            />
            <CardLink
              icon={<Webhook size={22} />}
              title="Webhooks"
              description="Configure notificações em tempo real via webhooks."
              count={webhooksCount}
              to="/integrations/webhooks"
            />
            <CardLink
              icon={<Download size={22} />}
              title="Exportações"
              description="Exporte dados do sistema nos formatos disponíveis."
              count={exportsCount}
              lastItem={lastExport}
              to="/integrations/exports"
            />
            <CardLink
              icon={<Upload size={22} />}
              title="Importações"
              description="Importe arquivos CSV, XLSX, XML ou JSON."
              count={importsCount}
              lastItem={lastImport}
              to="/integrations/imports"
            />
          </>
        )}
      </div>

      {/* Atividade Recente */}
      <div className="activity-section">
        <div className="section-header">
          <h2>Atividade Recente</h2>
        </div>
        <Card variant="elevated" padding="0">
          <CardBody>
            {isLoading ? (
              <div className="activity-skeleton">
                <LoadingSkeleton count={5} height="36px" />
              </div>
            ) : !data || data.recentActivity.length === 0 ? (
              <div className="activity-empty">
                <p>Nenhuma atividade recente.</p>
              </div>
            ) : (
              <div className="activity-table">
                <div className="activity-header">
                  <span className="activity-kind" />
                  <span className="activity-desc">Descrição</span>
                  <span>Status</span>
                  <span className="activity-date">Data</span>
                </div>
                {data.recentActivity.map((event) => (
                  <ActivityRow key={`${event.kind}-${event.id}`} event={event} />
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <style>{`
        .integration-dashboard {
          padding: 1.5rem;
        }

        /* Error banner */
        .error-banner {
          margin-bottom: 1rem;
        }
        .error-banner .card-body-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }
        .error-text {
          color: #ba1a1a;
          font-size: 0.875rem;
          margin: 0;
        }

        /* Cards grid */
        .cards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .integration-card {
          transition: box-shadow 0.2s, transform 0.15s;
        }
        .integration-card:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.12);
          transform: translateY(-2px);
        }

        .card-header-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
        }
        .card-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: #f2f4f6;
          color: #002045;
          border-radius: 8px;
        }
        .card-count {
          font-size: 1.75rem;
          font-weight: 700;
          color: #191c1e;
        }

        .card-title {
          margin: 0.5rem 0 0.25rem 0;
          font-size: 1rem;
          font-weight: 600;
          color: #191c1e;
        }
        .card-desc {
          margin: 0;
          font-size: 0.8125rem;
          color: #43474e;
          line-height: 1.4;
        }
        .card-last {
          margin: 0.375rem 0 0 0;
          font-size: 0.75rem;
          color: #74777f;
        }
        .card-action {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          margin-top: 0.75rem;
          font-size: 0.8125rem;
          font-weight: 500;
          color: #002045;
        }

        /* Activity section */
        .activity-section {
          margin-top: 0;
        }
        .section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 0.75rem;
        }
        .section-header h2 {
          font-size: 1.125rem;
          font-weight: 600;
          color: #191c1e;
          margin: 0;
        }

        .activity-skeleton {
          padding: 0.75rem 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .activity-empty {
          padding: 2rem;
          text-align: center;
          color: #74777f;
          font-size: 0.875rem;
        }

        .activity-table {
          font-size: 0.8125rem;
        }
        .activity-header,
        .activity-row {
          display: grid;
          grid-template-columns: 36px 1fr 110px 150px;
          gap: 0.75rem;
          padding: 0.625rem 1rem;
          align-items: center;
        }
        .activity-header {
          background: #f2f4f6;
          font-weight: 600;
          color: #43474e;
          border-bottom: 1px solid #e0e3e5;
        }
        .activity-row {
          border-bottom: 1px solid #eceef0;
          transition: background 0.15s;
        }
        .activity-row:hover {
          background: #f2f4f6;
        }
        .activity-row:last-child {
          border-bottom: none;
        }
        .activity-kind {
          display: flex;
          align-items: center;
          color: #74777f;
        }
        .activity-desc {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          color: #191c1e;
        }
        .activity-date {
          color: #74777f;
          white-space: nowrap;
        }
      `}</style>
    </div>
  );
}